import type {
	RequestConfig,
	ResponseConfig,
	ResponseErrorConfig,
} from "@kubb/plugin-client/clients/axios";
import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from "axios";

export type { RequestConfig, ResponseConfig, ResponseErrorConfig };

declare module "axios" {
	interface InternalAxiosRequestConfig {
		_retry?: boolean;
	}
}

export interface ApiClientConfig {
	baseURL: string;
	getToken: () => Promise<string | null>;
	onError?: (error: AxiosError) => void;
	onResponse?: (response: AxiosResponse) => void;
	onRequestError?: (error: AxiosError) => void;
}

let apiClient: AxiosInstance | null = null;
let globalGetToken: (() => Promise<string | null>) | null = null;
let globalOnError: ((error: AxiosError) => void) | null = null;
let globalOnResponse: ((response: AxiosResponse) => void) | null = null;
let globalOnRequestError: ((error: AxiosError) => void) | null = null;
let globalBaseURL: string | null = null;

export function configureApiClient(config: ApiClientConfig): AxiosInstance {
	globalGetToken = config.getToken;
	globalOnError = config.onError ?? null;
	globalOnResponse = config.onResponse ?? null;
	globalOnRequestError = config.onRequestError ?? null;
	globalBaseURL = config.baseURL;

	apiClient = axios.create({
		baseURL: config.baseURL,
		headers: { "Content-Type": "application/json" },
	});

	apiClient.interceptors.request.use(async (requestConfig) => {
		try {
			if (globalGetToken) {
				const token = await globalGetToken();
				if (token) {
					requestConfig.headers = requestConfig.headers || {};
					requestConfig.headers.Authorization = `Bearer ${token}`;
				}
			}
		} catch {
			// Proceed without token
		}
		return requestConfig;
	});

	apiClient.interceptors.response.use(
		(response: AxiosResponse) => {
			globalOnResponse?.(response);
			return response;
		},
		async (error: AxiosError) => {
			const originalRequest = error.config;

			if (
				error.response?.status === 401 &&
				originalRequest &&
				!originalRequest._retry &&
				globalGetToken
			) {
				originalRequest._retry = true;
				try {
					const newToken = await globalGetToken();
					if (newToken && originalRequest.headers && apiClient) {
						originalRequest.headers.Authorization = `Bearer ${newToken}`;
						return apiClient(originalRequest);
					}
				} catch {
					// Token refresh failed
				}
			}

			globalOnError?.(error);
			globalOnRequestError?.(error);
			return Promise.reject(error);
		},
	);

	return apiClient;
}

export function getApiClient(): AxiosInstance {
	if (!apiClient) {
		throw new Error("API client not configured. Call configureApiClient() first.");
	}
	return apiClient;
}

let internalConfig: Partial<RequestConfig<unknown>> = {};

async function apiClientFetchFn<TData, _TError = unknown, TVariables = unknown>(
	config: RequestConfig<TVariables>,
): Promise<{
	data: TData;
	status: number;
	statusText: string;
	headers: AxiosResponse["headers"];
}> {
	const client = getApiClient();
	const mergedConfig = { ...internalConfig, ...config };
	const response = await client.request<TData>({
		...mergedConfig,
		url: mergedConfig.url,
		data: mergedConfig.data as TVariables,
	});
	return {
		data: response.data,
		status: response.status,
		statusText: response.statusText,
		headers: response.headers,
	};
}

export const apiClientFetch = Object.assign(apiClientFetchFn, {
	getConfig: () => internalConfig,
	setConfig: (config: RequestConfig) => {
		internalConfig = { ...internalConfig, ...config };
		return internalConfig;
	},
}) as typeof import("@kubb/plugin-client/clients/axios").default;

export function getBaseURL(): string {
	if (!globalBaseURL) {
		throw new Error("API client not configured. Call configureApiClient() first.");
	}
	return globalBaseURL;
}

export default apiClientFetch;

export { apiClient };
